/**
 * The reward ladder's verbs (RC2-04) — salvaging the axe, and chopping wood.
 *
 * The demo is meant to be a story of earning: a player starts with almost
 * nothing, and getting an **axe** is a genuine event that opens a craft — woodcraft
 * — they did not have. This module is the world half of that. The axe is not a
 * quest reward an Elder hands over (owner decision: a *discoverable cache*, no
 * dialogue gate); it is lodged in a fallen hazel, and two verbs act on that
 * deadfall:
 *
 * - **`salvage`** takes the cached axe. Instant, like `feed` — picking a tool up
 *   is not a job — and legible: it emits an `interacted/['salvage','axe', …]`
 *   line the chronicle keeps and the client reads out, because the whole point of
 *   the ticket is that acquiring the axe is a *moment*, not a silent inventory
 *   add. The instant the axe is in the satchel, the sheet's woodcraft door opens
 *   (the "carries an axe" predicate in `snapshot.ts` reads the same `felling`
 *   tag `chop` does).
 *
 * - **`chop`** fells the wood. Channelled like `butcher`, gated on the axe being
 *   in hand, and it is what *builds* the woodcraft tally: the `chop/done` event
 *   it emits is the one `practice.ts`'s `familiarityForEvent` reads and the one
 *   `errands.ts` closes the deadfall errand off. No axe, no chop, no accrual — so
 *   the door gates the tally by construction, exactly as RC2-02 describes
 *   ("acquisition + use").
 *
 * The two together are the ladder in miniature: the tool opens the door, and the
 * doing walks through it.
 */
import { chebyshev } from './actor.js';
import { parseChop, parseSalvage } from './actions.js';
import { busyFault, hasTag } from './camp.js';
import { addItem, createInventory } from './item.js';
import { TICKS_PER_SECOND } from './time.js';
/**
 * The tag the axe carries, and the tool `chop` and the woodcraft door ask after.
 *
 * A tag rather than the item id `'axe'`, for the reason every capability in the
 * satchel is a tag: it is the *felling* a rule wants, and a later, better axe
 * should open the same door without this module learning its name.
 */
export const FELLING_TAG = 'felling';
/** A real job with an edged tool, on the order of butchery. */
export const CHOP_TICKS = TICKS_PER_SECOND * 15;
/** Bundles of firewood a single swing brings down. */
export const CHOP_YIELD = 1;
/** How much wood a freshly-seeded deadfall holds — swings to clear it. */
export const DEADFALL_PORTIONS = 5;
/**
 * Put a deadfall in the world (RC2-04).
 *
 * The one seam that places the reward ladder's prop. Used by tests today and by
 * the glen's arrival scene when that lands — the same shape either way, because a
 * deadfall is world state and knows nothing about who seeded it. Defaults are the
 * glen's: an axe cached in it, and `DEADFALL_PORTIONS` of wood to clear.
 */
export function seedDeadfall(world, spec) {
    const deadfall = {
        id: spec.id ?? 'deadfall',
        x: spec.x,
        y: spec.y,
        axe: spec.axe === undefined ? 'axe' : spec.axe,
        bow: spec.bow ?? null,
        portions: spec.portions ?? DEADFALL_PORTIONS,
    };
    world.deadfalls[deadfall.id] = deadfall;
    return deadfall;
}
/**
 * A deadfall within reach, or `null`.
 *
 * First in id order rather than nearest, like `carcassInReach` and for the same
 * reason: two deadfalls at your feet is not a thing the glen arranges, and a tie
 * broken by distance would still have to break the next tie somehow. Sorting by
 * id makes the choice a fact about the world, not about insertion order.
 */
export function deadfallInReach(world, actor) {
    for (const id of Object.keys(world.deadfalls).sort()) {
        const deadfall = world.deadfalls[id];
        if (!deadfall)
            continue;
        if (chebyshev(actor.x, actor.y, deadfall.x, deadfall.y) <= 1)
            return deadfall;
    }
    return null;
}
/* -------------------------------------------------------------------------- *
 * Salvage — taking a cached tool
 * -------------------------------------------------------------------------- */
/** Why no cached tool can be taken from here, or `null`. */
export function salvageFault(world, catalogue, actor, tick) {
    const busy = busyFault(world, catalogue, actor, tick, 'salvage');
    if (busy !== null)
        return busy;
    const deadfall = deadfallInReach(world, actor);
    if (!deadfall)
        return 'there is nothing here to salvage';
    if (deadfall.axe === null && deadfall.bow == null) {
        return 'there is nothing left in it worth taking';
    }
    return null;
}
export const salvageHandler = {
    type: 'salvage',
    parse: parseSalvage,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return 'you are not in the world';
        return salvageFault(ctx.world, ctx.catalogue, actor, ctx.tick);
    },
    // Instant, like `feed`: taking a tool in hand is a moment, not a job — and a
    // channel would mean the axe could be interrupted out of your hands by a wolf,
    // which is not the beat this verb is for.
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return;
        const deadfall = deadfallInReach(ctx.world, actor);
        if (!deadfall || (deadfall.axe === null && deadfall.bow == null))
            return;
        const cachedId = deadfall.axe ?? deadfall.bow;
        if (cachedId == null)
            return;
        const cacheKind = deadfall.axe === null ? 'bow' : 'axe';
        if (cacheKind === 'axe')
            deadfall.axe = null;
        else
            deadfall.bow = null;
        const inventory = (ctx.world.inventories[actor.id] ??= createInventory());
        const def = ctx.catalogue.item?.(cachedId);
        // A found tool is a keepsake of sorts — it never merges into a stack, so the
        // one you pulled from the deadfall is the one in your hand.
        addItem(inventory, cachedId, 1, ctx.tick, def?.stackable ?? false);
        const weapon = ctx.catalogue.weapon(cachedId);
        const combatant = ctx.world.combat[actor.id];
        if (weapon?.ranged && combatant) {
            combatant.loadout = { ...combatant.loadout, weaponId: cachedId, ammunition: 6 };
        }
        // The legible event (AC-2): a chronicle line worth reading, not a silent add.
        // `witnesses` so anyone by the coppice sees it happen, and the woodcraft door
        // on the sheet opens on the same tick — the predicate reads the `felling` tag
        // this axe now carries.
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['salvage', cacheKind, deadfall.id],
            text: cacheKind === 'bow'
                ? `${actor.name} draws the yew bow and its six arrows free of the deadfall. They are theirs now.`
                : `${actor.name} works the axe free of the deadfall. It is theirs now.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
    },
};
/* -------------------------------------------------------------------------- *
 * Chop — felling the wood
 * -------------------------------------------------------------------------- */
/** Why the deadfall cannot be chopped from here, or `null`. */
export function chopFault(world, catalogue, actor, tick) {
    const busy = busyFault(world, catalogue, actor, tick, 'chop');
    if (busy !== null)
        return busy;
    // The door: no felling tool in hand, no chopping. The whole of RC2-02's
    // "acquisition + use" gate lives in this line — woodcraft cannot accrue until
    // the axe is carried, because the event that grows it cannot be emitted.
    if (!hasTag(world, catalogue, actor.id, FELLING_TAG))
        return 'you have no axe to chop with';
    const deadfall = deadfallInReach(world, actor);
    if (!deadfall)
        return 'there is nothing here to chop';
    if (deadfall.portions <= 0)
        return 'the deadfall is cleared';
    return null;
}
export const chopHandler = {
    type: 'chop',
    parse: parseChop,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return 'you are not in the world';
        return chopFault(ctx.world, ctx.catalogue, actor, ctx.tick);
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        const combatant = actor ? ctx.world.combat[actor.id] : undefined;
        if (!actor || !combatant)
            return;
        const deadfall = deadfallInReach(ctx.world, actor);
        if (!deadfall)
            return;
        combatant.channel = {
            verb: 'chop',
            targetId: null,
            startedAt: ctx.tick,
            completesAt: ctx.tick + CHOP_TICKS,
            fromX: actor.x,
            fromY: actor.y,
            detail: deadfall.id,
        };
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['chop', 'begin', deadfall.id],
            text: `${actor.name} sets to on the fallen hazel.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
    },
};
/**
 * A swing landed — the finish half, dispatched from `interact.ts`'s channel loop.
 *
 * Re-checks the fault (the axe could have been dropped, the deadfall cleared by
 * somebody else) and, on the clear, spends one portion, drops firewood in the
 * satchel, and emits the two events the rest of the ladder reads: a `yield` line
 * for the wood, and the `chop/done` line that grows the woodcraft tally
 * (`practice.ts`) and closes the deadfall errand (`errands.ts`).
 */
export function finishChop(ctx, actor) {
    const fault = chopFault(ctx.world, ctx.catalogue, actor, ctx.tick);
    if (fault !== null) {
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['chop', 'failed', fault],
            text: `${actor.name} lowers the axe.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
        return;
    }
    const deadfall = deadfallInReach(ctx.world, actor);
    deadfall.portions = Math.max(0, deadfall.portions - 1);
    const inventory = (ctx.world.inventories[actor.id] ??= createInventory());
    const wood = ctx.catalogue.item?.('firewood');
    if (wood)
        addItem(inventory, wood.id, CHOP_YIELD, ctx.tick, wood.stackable);
    if (wood) {
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['chop', 'yield', wood.id, String(CHOP_YIELD)],
            text: `${actor.name} splits off a bundle of firewood.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
    }
    // The rung-building event. `done` is what `familiarityForEvent` maps to
    // woodcraft and what the deadfall errand's `chop` trigger closes on, so every
    // swing is one rep of the craft whether or not it was the swing that cleared
    // it. The last swing says so, for the log.
    const cleared = deadfall.portions <= 0;
    ctx.emit({
        type: 'interacted',
        actors: [actor.id],
        refs: ['chop', 'done', deadfall.id],
        text: cleared
            ? `${actor.name} clears the last of the deadfall. The path is open.`
            : `${actor.name} bites another wedge out of the hazel.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/* -------------------------------------------------------------------------- *
 * Registration
 * -------------------------------------------------------------------------- */
/** The verb this actor is doing, if it is one of ours — for the fault table's typing. */
export const WOODCRAFT_VERBS = ['salvage', 'chop'];
export const WOODCRAFT_INTENTS = [
    salvageHandler,
    chopHandler,
];
/** No systems of its own — the channel loop in `interact.ts` runs `finishChop`. */
export const WOODCRAFT_SYSTEMS = [];
//# sourceMappingURL=woodcraft.js.map